import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { format, isValid, parse, parseISO } from 'date-fns'
import TextBox from 'devextreme-react/text-box'
import DateBox from 'devextreme-react/date-box'
import Button from 'devextreme-react/button'
import { Popup } from 'devextreme-react/popup'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import { editData } from '../../utils/model-data'
import notify from '../../utils/notify'

const DATETIME_DISPLAY_FORMAT = 'yyyy-MM-dd HH:mm:ss'

function parseDatetimeValue(v: unknown): Date | null {
	if (v === null || v === undefined || v === '') {
		return null
	}
	if (v instanceof Date) {
		return isValid(v) ? v : null
	}
	if (typeof v === 'number') {
		const d = new Date(v)
		return isValid(d) ? d : null
	}
	if (typeof v === 'string') {
		const trimmed = v.trim()
		if (!trimmed) {
			return null
		}
		let d = parse(trimmed, DATETIME_DISPLAY_FORMAT, new Date())
		if (isValid(d)) {
			return d
		}
		d = parseISO(trimmed)
		if (isValid(d)) {
			return d
		}
		d = new Date(trimmed)
		return isValid(d) ? d : null
	}
	return null
}

/** One command argument: `key` is the property name stored in the JSON object. */
export type JsonArgumentDef = {
	key: string
	label?: string
	type?: 'string' | 'number' | 'boolean' | 'datetime'
}

/** @deprecated Use JsonArgumentDef */
export type JsonKeyDef = JsonArgumentDef

export type JsonFieldInfo = {
	arguments: JsonArgumentDef[]
}

function normalizeJsonArgType(raw: unknown): JsonArgumentDef['type'] {
	if (raw == null || typeof raw !== 'string') {
		return 'string'
	}
	const t = raw.trim().toLowerCase()
	if (t === 'boolean' || t === 'bool') {
		return 'boolean'
	}
	if (t === 'number' || t === 'integer' || t === 'int' || t === 'float' || t === 'double') {
		return 'number'
	}
	if (t === 'string' || t === 'text') {
		return 'string'
	}
	if (t === 'datetime') {
		return 'datetime'
	}
	return 'string'
}

/** JSON booleans often arrive as strings; `Boolean("false")` is true — use explicit coercion. */
function coerceBoolean(v: unknown): boolean {
	if (v === true || v === 1) {
		return true
	}
	if (v === false || v === 0 || v == null) {
		return false
	}
	if (typeof v === 'string') {
		const s = v.trim().toLowerCase()
		if (s === 'true' || s === '1' || s === 'yes' || s === 'on') {
			return true
		}
		if (s === 'false' || s === '0' || s === 'no' || s === 'off' || s === '') {
			return false
		}
	}
	return Boolean(v)
}

/** API may return `schema` (key → type), or legacy `arguments` / `keys`. */
function normalizeJsonFieldInfo(data: Record<string, unknown>): JsonFieldInfo {
	const schemaRaw = data.schema
	if (schemaRaw != null && typeof schemaRaw === 'object' && !Array.isArray(schemaRaw)) {
		const args: JsonArgumentDef[] = Object.entries(schemaRaw as Record<string, unknown>).map(([key, type]) => ({
			key,
			type: normalizeJsonArgType(type),
		}))
		return { arguments: args }
	}
	const rawArgs = (data.arguments ?? data.keys ?? []) as JsonArgumentDef[]
	const args = Array.isArray(rawArgs)
		? rawArgs.map(a => (a && typeof a === 'object' ? { ...a, type: normalizeJsonArgType(a.type) } : a))
		: []
	return {
		arguments: args as JsonArgumentDef[],
	}
}

export function parseJsonValue(raw: unknown): Record<string, unknown> {
	if (raw == null || raw === '') {
		return {}
	}
	if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
		return raw as Record<string, unknown>
	}
	if (typeof raw === 'string') {
		try {
			const parsed = JSON.parse(raw)
			if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				return parsed as Record<string, unknown>
			}
		} catch {
			return {}
		}
	}
	return {}
}

function formatValueForInput(v: unknown, type?: string): string {
	if (v === null || v === undefined || v === '') {
		return ''
	}
	if (type === 'boolean') {
		return coerceBoolean(v) ? 'true' : 'false'
	}
	if (type === 'datetime') {
		const d = parseDatetimeValue(v)
		return d ? format(d, DATETIME_DISPLAY_FORMAT) : ''
	}
	if (typeof v === 'object') {
		return JSON.stringify(v)
	}
	return String(v)
}

function coerceValue(raw: string, type?: string): unknown {
	const t = type || 'string'
	if (t === 'boolean') {
		return raw === 'true' || raw === '1'
	}
	if (t === 'number') {
		if (raw.trim() === '') {
			return null
		}
		const n = Number(raw)
		return Number.isFinite(n) ? n : null
	}
	return raw
}

type RowModel = { def: JsonArgumentDef; value: unknown }

function buildRows(parsed: Record<string, unknown>, info: JsonFieldInfo): RowModel[] {
	const defs = info.arguments || []
	return defs.map(def => ({
		def,
		value: parsed[def.key],
	}))
}

function rowsToObject(rows: RowModel[]): Record<string, unknown> {
	const out: Record<string, unknown> = {}
	for (const row of rows) {
		const t = row.def.type || 'string'
		if (t === 'boolean') {
			out[row.def.key] = coerceBoolean(row.value)
		} else if (t === 'string') {
			const stringValue = String(row.value ?? '')
			if (stringValue.trim() === '') {
				continue
			}
			out[row.def.key] = stringValue
		} else if (t === 'number') {
			if (row.value === null || row.value === undefined) {
				continue
			}
			const rawNumber = String(row.value).trim()
			if (rawNumber === '') {
				continue
			}
			const numberValue = typeof row.value === 'number' ? row.value : Number(rawNumber)
			if (!Number.isFinite(numberValue)) {
				continue
			}
			out[row.def.key] = numberValue
		} else if (t === 'datetime') {
			const d = parseDatetimeValue(row.value)
			if (!d) {
				continue
			}
			out[row.def.key] = format(d, DATETIME_DISPLAY_FORMAT)
		} else {
			out[row.def.key] = coerceValue(String(row.value ?? ''), t)
		}
	}
	return out
}

export interface JsonEditorProps {
	onHide: () => void
	onSaved?: () => void
	/** Same rules as grid column editing (locks, field permissions, etc.). */
	allowSave: boolean
	pKey: string | string[]
	folder: string
	model: string
	name: string
	values: Record<string, unknown>
}

const rowStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	gap: 8,
	marginBottom: 6,
	minWidth: 280,
}

const argumentNameStyle: React.CSSProperties = {
	flex: '0 0 150px',
	fontSize: 14,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	color: '#404040',
}

export default function JsonEditor({ onHide, onSaved, allowSave, pKey, folder, model, name, values }: JsonEditorProps) {
	const keyValue = Array.isArray(pKey) ? pKey.map((k: string) => values[k]) : values[pKey]
	const rawFieldValue = values[name]

	const [info, setInfo] = useState<JsonFieldInfo | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [rows, setRows] = useState<RowModel[]>([])
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const savingLockedRef = useRef(false)

	const rowsRef = useRef<RowModel[]>(rows)
	useEffect(() => {
		rowsRef.current = rows
	}, [rows])

	/**
	 * Re-fetch only when serialized data meaningfully changes. Using `values` by reference as an
	 * effect dependency re-ran the load on every parent render (new object identity) and wiped edits.
	 * Full-row JSON stays stable while toggling a Switch until Save (grid row is not mutated live).
	 */
	const fieldLoadKey = JSON.stringify({ folder, model, field: name, row: values })

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		setLoadError(null)
		axios
			.post<Record<string, unknown>>(`${API_FIBER_URL}/view/json-field`, {
				folder,
				model,
				field: name,
				row: values,
			})
			.then((res: { data: Record<string, unknown> }) => {
				if (cancelled) {
					return
				}
				const normalized = normalizeJsonFieldInfo(res.data)
				setInfo(normalized)
				const merged =
					res.data.values != null && typeof res.data.values === 'object' && !Array.isArray(res.data.values)
						? (res.data.values as Record<string, unknown>)
						: parseJsonValue(rawFieldValue)
				setRows(buildRows(merged, normalized))
			})
			.catch(() => {
				if (cancelled) {
					return
				}
				const fallback: JsonFieldInfo = { arguments: [] }
				setInfo(fallback)
				setLoadError(
					'Could not load field definition; you can edit values for arguments already stored in JSON.'
				)
				setRows(buildRows(parseJsonValue(rawFieldValue), fallback))
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false)
				}
			})
		return () => {
			cancelled = true
		}
	}, [fieldLoadKey])

	const updateRowAt = useCallback((index: number, patch: Partial<RowModel>) => {
		setRows(prev => {
			const next = [...prev]
			const cur = next[index]
			if (!cur) {
				return prev
			}
			next[index] = { ...cur, ...patch }
			rowsRef.current = next
			return next
		})
	}, [])

	const flushPendingInputAndSave = useCallback(() => {
		if (!allowSave || !info || loading || savingLockedRef.current) {
			return
		}
		savingLockedRef.current = true
		const ae = document.activeElement
		if (ae instanceof HTMLElement) {
			ae.blur()
		}
		setSaving(true)
		const finalize = () => {
			const obj = rowsToObject(rowsRef.current)
			const jsonPayload = Object.keys(obj).length === 0 ? '' : JSON.stringify(obj)
			const fieldValue: Record<string, string> = {}
			fieldValue[name] = jsonPayload
			editData('view', { key: keyValue, values: fieldValue, folder, model })
				.then(() => {
					notify('Saved', 'success')
					values[name] = jsonPayload
					onSaved?.()
					onHide()
				})
				.catch((err: string) => {
					notify(typeof err === 'string' ? err : 'Save failed', 'error')
				})
				.finally(() => {
					savingLockedRef.current = false
					setSaving(false)
				})
		}
		requestAnimationFrame(() => requestAnimationFrame(finalize))
	}, [allowSave, info, loading, name, keyValue, folder, model, values, onHide, onSaved])

	const renderContent = useCallback(() => {
		if (loading || !info) {
			return (
				<div className="content-block">
					<LoadIndicator height={20} width={20} />
				</div>
			)
		}

		return (
			<div>
				{loadError && <div style={{ fontSize: 12, color: '#c45c5c', marginBottom: 8 }}>{loadError}</div>}
				<div
					style={{
						padding: '4px 0',
						maxHeight: 320,
						overflowY: 'auto',
						boxSizing: 'border-box',
					}}
				>
					{rows.length === 0 && <div style={{ fontSize: 12, color: '#888' }}>No data to edit.</div>}
					{rows.map((row, index) => (
						<div key={row.def.key} style={rowStyle}>
							<span style={argumentNameStyle} title={row.def.key}>
								{row.def.label || row.def.key}
							</span>
							<div style={{ flex: 1, minWidth: 0 }}>
								{(row.def.type || 'string') === 'boolean' ? (
									<label
										style={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: 6,
											margin: 0,
											cursor: allowSave ? 'pointer' : 'default',
											userSelect: 'none',
										}}
									>
										<input
											type="checkbox"
											checked={coerceBoolean(row.value)}
											disabled={!allowSave}
											onChange={e => {
												e.stopPropagation()
												updateRowAt(index, { value: e.target.checked })
											}}
										/>
										<span style={{ fontSize: 12, color: '#666' }}>
											{coerceBoolean(row.value) ? 'Yes' : 'No'}
										</span>
									</label>
								) : (row.def.type || 'string') === 'datetime' ? (
									<DateBox
										value={parseDatetimeValue(row.value) ?? undefined}
										type="datetime"
										displayFormat={DATETIME_DISPLAY_FORMAT}
										invalidDateMessage=""
										valueChangeEvent="keyup change"
										readOnly={!allowSave}
										showClearButton={true}
										onValueChanged={e => {
											const d = parseDatetimeValue(e.value)
											updateRowAt(index, {
												value: d ? format(d, DATETIME_DISPLAY_FORMAT) : '',
											})
										}}
									/>
								) : (
									<TextBox
										value={formatValueForInput(row.value, row.def.type)}
										valueChangeEvent="keyup change"
										readOnly={!allowSave}
										onValueChanged={e =>
											updateRowAt(index, {
												value: coerceValue(e.value ?? '', row.def.type || 'string'),
											})
										}
									/>
								)}
							</div>
						</div>
					))}
				</div>
				<div
					style={{
						display: 'flex',
						gap: 8,
						marginTop: 12,
						justifyContent: 'space-between',
						alignItems: 'center',
						borderTop: '1px solid rgba(0,0,0,0.08)',
						paddingTop: 12,
					}}
				>
					<div style={{ minWidth: 90, display: 'flex', alignItems: 'center' }}>
						{saving && (
							<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
								<LoadIndicator height={18} width={18} />
								<span style={{ fontSize: 12, color: '#666' }}>Saving...</span>
							</div>
						)}
					</div>
					<div style={{ display: 'flex', gap: 8 }}>
						<Button text="Cancel" onClick={onHide} disabled={saving} />
						{rows.length > 0 && (
							<Button
								text="Save"
								type="default"
								stylingMode="contained"
								icon="save"
								onClick={flushPendingInputAndSave}
								disabled={saving || !allowSave || loading}
							/>
						)}
					</div>
				</div>
			</div>
		)
	}, [loading, info, loadError, saving, rows, allowSave, name, onHide, updateRowAt, flushPendingInputAndSave])

	return (
		<Popup
			visible={true}
			dragEnabled={true}
			resizeEnabled={true}
			defaultWidth="80%"
			defaultHeight="50%"
			showCloseButton={false}
			contentRender={renderContent}
			title={`Edit data for ${name}`}
			position="center"
			hideOnOutsideClick={() => true}
			onHiding={onHide}
		/>
	)
}
